import { Activity, AlertTriangle, Clock, MemoryStick, Wifi } from "lucide-react";
import { useCallback } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { SeverityBadge } from "@/components/shared/SeverityBadge";
import { StatCard } from "@/components/shared/StatCard";
import { StatusTracker } from "@/components/monitoring/StatusTracker";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { calculateTrend, formatBytes, formatCurrency, formatDuration, generateColorScale } from "@/lib/chart-helpers";
import type { Alert, AlertSeverity, ChannelHealthMetrics, HeatmapDay } from "@/types/metrics";

// Backend response shapes (nested, differs from frontend flat types)
type BackendSample = {
  ts: number;
  cpu: { user: number; system: number; percent: number };
  memory: { rss: number; heapUsed: number; heapTotal: number; external: number; percentUsed: number };
  disk: { total: number; free: number; used: number; percentUsed: number };
  uptime: number;
  pid: number;
  eventLoopLag: number | null;
};

type BackendSummary = {
  totalRuns: number;
  successCount: number;
  errorCount: number;
  successRate: number;
  avgDurationMs: number;
  totalCost: number;
  totalTokens: number;
  byModel: Record<string, { runs: number; cost: number; tokens: number }>;
  byAgent: Record<string, { runs: number; cost: number }>;
};

type DashboardData = {
  samples: BackendSample[];
  agents: BackendSummary;
  heatmap: HeatmapDay[];
  alerts: Alert[];
  channels: ChannelHealthMetrics[];
};

const PIE_COLORS = generateColorScale(8);

export function Dashboard() {
  const { rpc, state } = useGatewayRpc();

  const fetcher = useCallback(async (): Promise<DashboardData> => {
    const [sys, agents, heatmap, alerts, channels] = await Promise.all([
      rpc<{ samples: BackendSample[] }>("metrics.system", { since: Date.now() - 1_800_000 }),
      rpc<{ events: unknown[]; summary: BackendSummary }>("metrics.agents", { since: Date.now() - 86_400_000 }),
      rpc<{ days: HeatmapDay[] }>("metrics.heatmap", { days: 30 }),
      rpc<{ alerts: Alert[] }>("metrics.alerts"),
      rpc<{ channels: ChannelHealthMetrics[] }>("metrics.channels"),
    ]);
    return {
      samples: sys.samples,
      agents: agents.summary,
      heatmap: heatmap.days,
      alerts: alerts.alerts,
      channels: channels.channels,
    };
  }, [rpc]);

  const { data, loading, error } = usePolling(fetcher, 5000, { enabled: state === "connected" });

  const latest = data?.samples.at(-1);
  const cpuSpark = data?.samples.map((s) => ({ value: s.cpu.percent })) ?? [];
  const memSpark = data?.samples.map((s) => ({ value: s.memory.percentUsed })) ?? [];
  const diskSpark = data?.samples.map((s) => ({ value: s.disk.percentUsed })) ?? [];

  const cpuTrend = cpuSpark.length > 1 ? calculateTrend(cpuSpark.map((s) => s.value)) : undefined;
  const memTrend = memSpark.length > 1 ? calculateTrend(memSpark.map((s) => s.value)) : undefined;

  const heatmapDays = (data?.heatmap ?? []).map((d) => {
    const total = d.hours.reduce((s, h) => s + h, 0);
    const status = total === 0 ? "unknown" : total < 5 ? "ok" : total < 20 ? "degraded" : "down";
    return { date: d.date, status: status as "ok" | "degraded" | "down" | "unknown" };
  });

  const modelPieData = Object.entries(data?.agents.byModel ?? {}).map(([model, v]) => ({
    name: model.split("-").slice(0, 2).join("-"),
    value: v.runs,
    cost: v.cost,
    tokens: v.tokens,
  }));

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Dashboard</h1>

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
          {error.message}
        </div>
      )}

      {/* System Metrics Row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="CPU"
          value={latest ? `${latest.cpu.percent.toFixed(1)}%` : "--"}
          trend={cpuTrend}
          sparkData={cpuSpark}
          sparkColor="var(--color-healthy)"
        />
        <StatCard
          title="Memory"
          value={latest ? `${latest.memory.percentUsed.toFixed(1)}%` : "--"}
          trend={memTrend}
          sparkData={memSpark}
          sparkColor="var(--color-warning)"
        />
        <StatCard
          title="Disk"
          value={latest ? `${latest.disk.percentUsed.toFixed(1)}%` : "--"}
          sparkData={diskSpark}
          sparkColor="var(--color-muted)"
        />
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Uptime</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-indigo-500" />
            <span className="text-2xl font-semibold tabular-nums">
              {latest ? formatDuration(latest.uptime * 1000) : "--"}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Agent Summary + Cost Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Agent Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Activity className="h-4 w-4" /> Agent Activity (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.agents.totalRuns === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">No agent runs in the last 24 hours</p>
            ) : (
              <div className="flex items-start gap-6">
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">{data?.agents.totalRuns ?? 0}</span> runs</p>
                  <p><span className="font-medium">{((data?.agents.successRate ?? 0) * 100).toFixed(0)}%</span> success</p>
                  <p><span className="font-medium">{formatDuration(data?.agents.avgDurationMs ?? 0)}</span> avg</p>
                  <p><span className="font-medium">{formatCurrency((data?.agents.totalCost ?? 0) * 100)}</span> cost</p>
                </div>
                {modelPieData.length > 0 && (
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie data={modelPieData} dataKey="value" cx="50%" cy="50%" outerRadius={50} innerRadius={25}>
                        {modelPieData.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => [`${v} runs`]} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Memory Stats */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <MemoryStick className="h-4 w-4" /> Memory Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            {latest ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">RSS</p>
                  <p className="font-medium">{formatBytes(latest.memory.rss)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Heap Used</p>
                  <p className="font-medium">{formatBytes(latest.memory.heapUsed)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Heap Total</p>
                  <p className="font-medium">{formatBytes(latest.memory.heapTotal)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">External</p>
                  <p className="font-medium">{formatBytes(latest.memory.external)}</p>
                </div>
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-slate-400">No data</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 30-Day Heatmap */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">30-Day Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {heatmapDays.length > 0 ? (
            <StatusTracker days={heatmapDays} />
          ) : (
            <p className="py-2 text-sm text-slate-400">No heatmap data</p>
          )}
        </CardContent>
      </Card>

      {/* Alerts + Channels Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Active Alerts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertTriangle className="h-4 w-4" /> Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.alerts ?? []).length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">No active alerts</p>
            ) : (
              <div className="space-y-2">
                {data?.alerts.map((alert) => (
                  <div key={alert.id} className="flex items-start gap-2 rounded-md border p-2 text-sm dark:border-slate-800">
                    <SeverityBadge severity={alert.severity as AlertSeverity} />
                    <div className="flex-1">
                      <p className="font-medium">{alert.message}</p>
                      <p className="text-xs text-muted-foreground">
                        Triggered {new Date(alert.triggeredAt).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Channel Health */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Wifi className="h-4 w-4" /> Channels
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.channels ?? []).length === 0 ? (
              <p className="py-4 text-center text-sm text-slate-400">No channels configured</p>
            ) : (
              <div className="space-y-1.5">
                {data?.channels.map((ch) => (
                  <div key={ch.channelId} className="flex items-center justify-between text-sm">
                    <span>{ch.label || ch.channelId}</span>
                    <Badge variant={ch.connected ? "default" : "secondary"}>
                      {ch.connected ? "Connected" : "Disconnected"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {loading && !data && (
        <p className="text-center text-sm text-slate-400">Loading metrics...</p>
      )}
    </div>
  );
}
